import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Unidade, UnidadeContextData } from '../types/unidade';
import { getUnidades } from '../helpers/api/unidadeApi';

const UnidadeContext = createContext<UnidadeContextData>({} as UnidadeContextData);

export const UnidadeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [unidadeSelecionada, setUnidadeSelecionadaState] = useState<Unidade | null>(null);
  const [unidadesDisponiveis, setUnidadesDisponiveis] = useState<Unidade[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const user = useSelector((state: any) => state.Auth?.user?.usuario);
  const [userId, setUserId] = useState<number | null>(null);

  const carregarUnidadesDisponiveis = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const { data: { result } } = await getUnidades();
      setUnidadesDisponiveis(result);
    } catch (err) {
      setError('Erro ao carregar unidades disponíveis');
      console.error('Erro ao carregar unidades:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      if (userId && user.id !== userId) {
        setUnidadesDisponiveis([]);
        setUnidadeSelecionadaState(null);
        localStorage.removeItem('unidadeSelecionada');
        setUserId(user.id);
        carregarUnidadesDisponiveis();
      } else if (!userId) {
        setUserId(user.id);
        carregarUnidadesDisponiveis();
      }
    } else if (userId) {
      setUnidadesDisponiveis([]);
      setUnidadeSelecionadaState(null);
      localStorage.removeItem('unidadeSelecionada');
      setUserId(null);
    }
  }, [user?.id, userId, carregarUnidadesDisponiveis]);

  useEffect(() => {
    const unidadeSalva = carregarUnidadeSelecionada();
    
    if (!unidadeSalva && user) {
      const buscarUnidadeUsuario = async () => {
        try {
          const { data: { result } } = await getUnidades();
          const unidadeUsuario = result.find((u: Unidade) => u.id === user.unidade_id);
          if (unidadeUsuario) {
            setUnidadeSelecionadaState(unidadeUsuario);
          }
        } catch (err) {
          console.error('Erro ao buscar unidade do usuário:', err);
        }
      };
      buscarUnidadeUsuario();
    } else {
      setUnidadeSelecionadaState(unidadeSalva);
    }
  }, [user?.unidade_id]);

  const salvarUnidadeSelecionada = (unidade: Unidade | null): void => {
    if (unidade) {
      localStorage.setItem('unidadeSelecionada', JSON.stringify(unidade));
    } else {
      localStorage.removeItem('unidadeSelecionada');
    }
  };

  const carregarUnidadeSelecionada = (): Unidade | null => {
    try {
      const unidade = localStorage.getItem('unidadeSelecionada');
      return unidade ? JSON.parse(unidade) : null;
    } catch {
      localStorage.removeItem('unidadeSelecionada');
      return null;
    }
  };

  const setUnidadeSelecionada = useCallback((unidade: Unidade | null) => {
    salvarUnidadeSelecionada(unidade);
    setUnidadeSelecionadaState(unidade);
    setError(null);
    
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }, []);

  return (
    <UnidadeContext.Provider
      value={{
        unidadeSelecionada,
        unidadesDisponiveis,
        setUnidadeSelecionada,
        carregarUnidadesDisponiveis,
        isLoading,
        error,
      }}
    >
      {children}
    </UnidadeContext.Provider>
  );
};

export const useUnidade = () => {
  const context = useContext(UnidadeContext);
  if (!context) {
    throw new Error('useUnidade deve ser usado dentro de um UnidadeProvider');
  }
  return context;
};
